/**
 * Decision actions shared by the phone detail and the desktop pane. After a T3 approval the
 * next item opens (with a short input lock); after a T4 approval there is no auto-advance.
 */

import type { Proposal } from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { canEdit, orderedProposals, requiresHold } from '../app/selectors.js';
import { copyText, useSheets } from '../components/sheets.js';

/** How the approve was triggered: a tap (T3 only) or the completed 1.5 s hold. */
export type ApproveVia = 'tap' | 'hold';

export function useDecision(
  proposal: Proposal | null,
  advance: (nextId: string | null) => void,
  options: { onHeldApproved?: (proposalId: string) => void } = {},
) {
  const store = useStore();
  const state = usePanel();
  const sheets = useSheets();
  const order = state.snapshot ? orderedProposals(state.snapshot).map((item) => item.id) : [];

  const nextAfter = (id: string): string | null => {
    const index = order.indexOf(id);
    if (index < 0) return null;
    return order[index + 1] ?? order[index - 1] ?? null;
  };

  const blocker = store.approveBlocker(proposal?.id ?? null);

  const approve = async (via: ApproveVia) => {
    if (!proposal || store.approveBlocker(proposal.id) !== null) return;
    const hold = requiresHold(proposal);
    // A tap never approves an item that needs the hold, whatever button was on screen.
    if (hold && via !== 'hold') return;
    const next = nextAfter(proposal.id);
    // The copy starts inside the click, while the browser still allows clipboard access; the
    // approval is recorded only once the text is really on the clipboard.
    // v0.1.114: a delivery message is not copied here; its linked text waits in Gönderilecekler.
    // v0.1.223: a new Etsy listing draft (a generic card) copies its title, tags and description.
    const copySource =
      proposal.executor.kind !== 'manual_copy' || proposal.executor.copyOnApprove === false
        ? null
        : proposal.kind === 'customer_reply'
          ? proposal.draft.en
          : proposal.kind === 'generic'
            ? (proposal.copyText ?? null)
            : null;
    const copied = copySource === null ? undefined : await copyText(copySource);
    const ticket = await store.approve(proposal.id, {
      confirmedByHold: via === 'hold',
      ...(copied === undefined ? {} : { copied }),
    });
    if (!ticket) return;
    navigator.vibrate?.(10);
    if (hold) {
      options.onHeldApproved?.(proposal.id);
      return;
    }
    store.lockInput();
    advance(next);
  };

  const edit = () => {
    if (!proposal || !canEdit(proposal)) return;
    const next = nextAfter(proposal.id);
    sheets.open({ kind: 'edit', proposalId: proposal.id, onApproved: () => advance(next) });
  };

  const reject = () => {
    if (!proposal) return;
    const next = nextAfter(proposal.id);
    sheets.open({ kind: 'reject', proposalId: proposal.id, onDone: () => advance(next) });
  };

  return { approve, edit, reject, blocker, order };
}
